import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Buildings, GridFour, MagnifyingGlass, Hash, Database,
  SignOut, ChatCircle, Archive, CaretDoubleLeft, CaretDoubleRight, ShieldCheck, Trophy, CurrencyInr, Brain, Shield, ListBullets, Key, Microphone,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/admin", icon: GridFour, label: "Control Room", end: true, testid: "nav-control-room" },
  { to: "/admin/data-entry", icon: Database, label: "Knowledge Entry", testid: "nav-data-entry" },
  { to: "/admin/archive", icon: Archive, label: "Knowledge Archive", testid: "nav-archive" },
  { to: "/admin/governance", icon: ShieldCheck, label: "Governance Replay", testid: "nav-governance" },
  { to: "/admin/golden", icon: Trophy, label: "Golden Questions", testid: "nav-golden" },
  { to: "/admin/spend", icon: CurrencyInr, label: "LLM Spend", testid: "nav-spend" },
  { to: "/admin/memory", icon: Brain, label: "Customer Memory", testid: "nav-memory" },
  { to: "/admin/gatekeeper", icon: Shield, label: "Gatekeeper", testid: "nav-gatekeeper" },
  { to: "/admin/sops", icon: ListBullets, label: "SOPs", testid: "nav-sops" },
  { to: "/admin/llm-keys", icon: Key, label: "LLM Key Admin", testid: "nav-llm-keys" },
  { to: "/admin/locations", icon: Buildings, label: "Complex Map", testid: "nav-locations" },
  { to: "/admin/hashtags", icon: Hash, label: "Hashtag Master", testid: "nav-hashtags" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("av_sidebar_collapsed") === "1";
  });

  useEffect(() => {
    localStorage.setItem("av_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const onLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside
        className={`relative bg-slate-900 text-slate-100 flex flex-col transition-[width] duration-200 ease-in-out shrink-0 ${
          collapsed ? "w-16" : "w-72"
        }`}
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Brand row */}
        <div className={`border-b border-slate-800 ${collapsed ? "px-3 py-5" : "px-6 py-6"} flex items-center justify-between gap-2`}>
          {!collapsed ? (
            <div className="min-w-0">
              <div className="mono-label text-slate-400 mb-1">3CCTech.ITC.AiVerse</div>
              <div className="font-display text-2xl text-white leading-none tracking-tight">
                Control<span className="text-orange-500">.</span>Room
              </div>
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <div className="w-8 h-8 bg-orange-600 flex items-center justify-center text-white font-display text-sm leading-none">3</div>
            </div>
          )}
        </div>

        {/* Collapse toggle — sits on the sidebar's right edge */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle-btn"
          className="absolute z-30 w-6 h-6 bg-slate-900 border border-slate-700 hover:border-orange-500 hover:text-orange-400 text-slate-200 flex items-center justify-center transition-all"
          style={{
            borderRadius: 2,
            top: 22,
            right: -12,
          }}
        >
          {collapsed ? <CaretDoubleRight size={12} weight="bold" /> : <CaretDoubleLeft size={12} weight="bold" />}
        </button>

        <nav className={`flex-1 py-6 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
          {links.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-3 text-sm border transition-all duration-200 ${
                  isActive
                    ? "bg-white text-slate-900 border-white"
                    : "text-slate-300 border-transparent hover:bg-slate-800 hover:text-white"
                }`
              }
              style={{ borderRadius: 2 }}
            >
              <Icon size={18} weight="regular" />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
          <NavLink
            to="/chat"
            data-testid="nav-chat"
            title={collapsed ? "User Chat" : undefined}
            className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-3 text-sm border border-transparent text-slate-300 hover:bg-slate-800 hover:text-white transition-all`}
            style={{ borderRadius: 2 }}
          >
            <ChatCircle size={18} />
            {!collapsed && <span className="truncate">User Chat</span>}
          </NavLink>
          <NavLink
            to="/voice-chat"
            data-testid="nav-voice-chat"
            title={collapsed ? "Voice Chat" : undefined}
            className={`flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-4"} py-3 text-sm border border-transparent text-slate-300 hover:bg-slate-800 hover:text-white transition-all`}
            style={{ borderRadius: 2 }}
          >
            <Microphone size={18} />
            {!collapsed && <span className="truncate">Voice Chat</span>}
          </NavLink>
        </nav>

        {/* Footer: signed-in + logout */}
        <div className={`pb-6 ${collapsed ? "px-2" : "px-3"}`}>
          {!collapsed && (
            <div className="px-4 py-3 border border-slate-800 mb-2" style={{ borderRadius: 2 }}>
              <div className="mono-label text-slate-500 mb-1">Signed in as</div>
              <div className="text-sm text-white truncate">{user?.email}</div>
              <div className="mono-label text-orange-500 mt-1">{user?.role?.toUpperCase()}</div>
            </div>
          )}
          {collapsed && (
            <div className="mb-2 flex justify-center" title={`${user?.email} · ${user?.role}`}>
              <div className="w-10 h-10 bg-slate-800 text-white border border-slate-700 flex items-center justify-center font-display text-sm" style={{ borderRadius: 2 }}>
                {(user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            title={collapsed ? "Log out" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-2 px-4"} py-3 text-sm text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent transition-all`}
            style={{ borderRadius: 2 }}
          >
            <SignOut size={18} />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-h-screen overflow-y-auto blueprint-grid relative">
        <header className="bg-white border-b border-slate-200 px-10 py-5 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className="w-2 h-8 bg-orange-600" />
            <div>
              <div className="mono-label">Address</div>
              <div className="font-mono text-sm text-slate-900">
                3CCTech.ITC.AiVerse <span className="text-slate-400">/</span>{" "}
                <span className="text-orange-600">admin</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MagnifyingGlass size={18} className="text-slate-400" />
            <div className="mono-label">v1.0 · BETA</div>
          </div>
        </header>
        <div className="px-10 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
